import { SectionEyebrow } from "../components/CommonComponents";
import { ArrowUpRight } from "../components/CommonComponents";
const serif = "font-['Libre_Caslon_Display',Georgia,serif]";

const CourseCurriculumStructure = ({ course }) => {
    const items = [
        {
            number: "01",
            type: "Foundation",
            title: "Core Academic Foundation",
            description:
                "Establish essential concepts, principles and foundational knowledge required for progressive study in the discipline.",
        },
        {
            number: "02",
            type: "Core",
            title: "Discipline Knowledge",
            description:
                "Develop deeper subject understanding through progressively structured discipline-focused academic learning.",
        },
        {
            number: "03",
            type: "Applied",
            title: "Practical Application",
            description:
                "Connect academic concepts with application through projects, assignments and programme-relevant practical experiences.",
        },
        {
            number: "04",
            type: "Advanced",
            title: "Advanced Learning",
            description:
                "Progress towards advanced areas of study while strengthening analytical thinking and problem-solving capabilities.",
        },
        {
            number: "05",
            type: "Integration",
            title: "Integrated Learning",
            description:
                "Bring knowledge and capabilities together through advanced assignments, projects, research or programme-specific activities.",
        },
        {
            number: "06",
            type: "Progression",
            title: "Academic & Career Progression",
            description:
                "Consolidate the learning experience and prepare for professional opportunities, higher studies and continued development.",
        },
    ];

    return (
        <section
            id="curriculum-structure"
            aria-labelledby="curriculum-structure-heading"
            className="relative overflow-hidden bg-[#EEF4F8] py-20 lg:py-28"
        >
            {/* Background architecture */}
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 left-[8%] hidden w-px bg-navy/[0.06] xl:block"
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-y-0 right-[8%] hidden w-px bg-navy/[0.06] xl:block"
            />

            <div className="container relative z-[1] mx-auto">
                {/* HEADER */}
                <div className="grid gap-10 lg:grid-cols-[0.7fr_1.3fr] lg:gap-16">
                    <div>
                        <SectionEyebrow>
                            Academic Framework
                        </SectionEyebrow>

                        <div className="mt-10 hidden items-center gap-4 lg:flex">
                            <span className="font-serif text-[52px] leading-none text-gold">
                                Syllabus
                            </span>

                            <span className="h-12 w-px bg-navy/15" />

                            <span className="max-w-[120px] text-sm font-semibold uppercase leading-[1.5] tracking-[0.12em] text-ink/45">
                                Learning Dimensions
                            </span>
                        </div>
                    </div>

                    <div>
                        <h2
                            id="curriculum-structure-heading"
                            className="max-w-[900px] font-serif text-[clamp(42px,5vw,70px)] font-normal leading-[1.04] tracking-[-0.04em] text-navy"
                        >
                            {course?.shortName || "Programme"}
                            <em className="font-normal italic text-burgundy">
                                Curriculum
                            </em>{" "}
                            &amp; Program Structure
                        </h2>

                        <div className="mt-7 grid gap-6 border-t border-navy/15 pt-7 md:grid-cols-[1fr_auto] md:items-start">
                            <p className="max-w-[690px] text-lg leading-[1.8] text-ink/65">
                                A structured academic framework designed to develop knowledge
                                progressively—from essential foundations to advanced,
                                application-oriented learning.
                            </p>

                            <div className="md:text-right">
                                <span className="block text-sm font-bold uppercase tracking-[0.13em] text-burgundy">
                                    Programme Duration
                                </span>

                                <strong className="mt-2 block font-serif text-[25px] font-normal text-navy">
                                    {course?.duration || "As Prescribed"}
                                </strong>
                            </div>
                        </div>
                    </div>
                </div>

                {/* CURRICULUM BLUEPRINT */}
                <div className="mt-14 border border-navy/15 bg-white lg:mt-16">
                    {/* Blueprint header */}
                    <div className="grid border-b border-navy/15 lg:grid-cols-[260px_1fr]">
                        <div className="flex items-center bg-navy px-7 py-6 text-white lg:px-9">
                            <div>
                                <span className="text-sm font-bold uppercase tracking-[0.15em] text-gold">
                                    Curriculum Blueprint
                                </span>

                                <p className="mt-2 text-sm leading-[1.6] text-white/50">
                                    Programme architecture
                                </p>
                            </div>
                        </div>

                        <div className="flex flex-col gap-4 px-7 py-6 md:flex-row md:items-center md:justify-between md:px-9">
                            <strong className="font-serif text-[24px] font-normal text-navy">
                                {course?.title || "Academic Programme"}
                            </strong>

                            <div className="flex items-center gap-3">
                                <span className="h-2 w-2 rotate-45 bg-gold" />
                                <span className="text-sm font-semibold uppercase tracking-[0.12em] text-ink/45">
                                    Progressive Academic Structure
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* BLUEPRINT BODY */}
                    <div className="bg-[#F8FAFB]">
                        <div className="flex h-full flex-col justify-between p-7 lg:p-9">
                            <div>
                                <span className="text-sm font-bold uppercase tracking-[0.14em] text-burgundy">
                                    Learning Progression
                                </span>

                                <h3 className="mt-5 font-serif text-[30px] font-normal leading-[1.2] text-navy">
                                    From foundation to{" "}
                                    <em className="font-normal italic text-burgundy">
                                        progression.
                                    </em>
                                </h3>

                                <p className="mt-5 text-base leading-[1.7] text-ink/55">
                                    Each dimension contributes to a connected learning
                                    experience rather than functioning as an isolated stage.
                                </p>
                            </div>

                        </div>
                    </div>

                    <div className="overflow-x-auto">
                        <div className="flex items-stretch">
                            {course.programStructure.map((x, i) =>
                                <div key={x.title} className={`relative flex-1 border-t border-l border-[#071A2F]/15 p-6 last:border-r ${i === course.programStructure.length - 1 ? "bg-[#8B1E3F] text-white" : "bg-white"}`}>
                                    <div>
                                        <span className="text-sm font-bold text-[#D5AD57]">{String(i + 1).padStart(2, "0")}</span>
                                        <h4 className={`${serif} mt-5 text-2xl`}>{x.title}</h4>
                                        <p className="mt-8 text-sm">{x.credits} credits</p>
                                        <strong className="mt-2 block">{x.marks} marks</strong>
                                    </div>
                                    {i < course.programStructure.length - 1 &&
                                        <span className="absolute -right-3 top-1/2 z-10 grid h-6 w-6 -translate-y-1/2 place-items-center rounded-full bg-[#D5AD57] text-[#071A2F]">→</span>
                                    }
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* CURRICULUM FLOW */}
                <div className="grid border-x border-b border-navy/15 bg-white lg:grid-cols-[260px_1fr]">
                    <div className="flex items-center border-b border-navy/15 px-7 py-7 lg:border-b-0 lg:border-r lg:px-9">
                        <span className="text-sm font-bold uppercase tracking-[0.14em] text-burgundy">
                            Learning Flow
                        </span>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4">
                        {["Learn", "Understand", "Apply", "Progress"].map(
                            (label, index) => (
                                <div
                                    key={label}
                                    className={`
                    relative
                    flex
                    min-h-[90px]
                    items-center
                    justify-center
                    gap-3
                    px-4
                    text-center

                    ${index !== 3 ? "md:border-r md:border-navy/15" : ""}
                    ${index < 2 ? "max-md:border-b max-md:border-navy/15" : ""}
                    ${index % 2 === 0 ? "max-md:border-r max-md:border-navy/15" : ""}
                  `}
                                >
                                    <span className="font-serif text-[20px] text-gold-dark">
                                        0{index + 1}
                                    </span>

                                    <strong className="text-sm font-bold uppercase tracking-[0.1em] text-navy">
                                        {label}
                                    </strong>

                                    {index !== 3 && (
                                        <span className="absolute right-[-8px] top-1/2 z-[2] hidden h-4 w-4 -translate-y-1/2 rotate-45 border-r border-t border-navy/15 bg-white md:block" />
                                    )}
                                </div>
                            )
                        )}
                    </div>
                </div>


                {/* NEXT SECTION CTA */}
                <div className="mt-10 grid overflow-hidden bg-burgundy text-white md:grid-cols-[1fr_auto] md:items-stretch">
                    <div className="px-7 py-7 md:px-9 lg:px-11">
                        <span className="text-sm font-bold uppercase tracking-[0.14em] text-gold">
                            Explore the Curriculum
                        </span>

                        <p className="mt-3 max-w-[750px] text-base leading-[1.7] text-white/75">
                            See how the academic framework translates into semester-wise
                            subjects and areas of study throughout{" "}
                            <strong className="font-semibold text-white">
                                {course?.title || "the programme"}.
                            </strong>
                        </p>
                    </div>

                    <a
                        href="#semester-syllabus"
                        className="group flex min-h-[82px] items-center justify-between gap-10 border-t border-white/15 bg-navy px-7 text-sm font-bold text-white transition-colors duration-300 hover:bg-gold hover:text-navy md:min-w-[250px] md:border-l md:border-t-0 md:px-8"
                    >
                        Semester Syllabus

                        <ArrowUpRight className="h-[19px] w-[19px] transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </a>
                </div>
            </div>
        </section>
    );
};

export default CourseCurriculumStructure;